import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Modal from "react-bootstrap/Modal";
import {
  MdAdd, MdClose, MdContentCopy, MdDelete, MdEdit, MdLayers, MdLock, MdPause, MdPlayArrow, MdSearch,
  MdVisibility, MdWarning, MdCheckCircle, MdImage, MdStorefront, MdGavel,
} from "react-icons/md";
import {
  listProductBundles, searchBundleProducts, simulateProductBundle, createProductBundle, updateProductBundle,
  setProductBundleStatus, deleteProductBundle, duplicateProductBundle, uploadProductBundleImage, removeProductBundleImage,
  updateProductBundleSettings, requestBundleOwnerAuthorization, approveBundleLoss, rejectBundleAuthorization, revokeBundleAuthorization,
} from "../../auth/apiProductBundles";
import { toImageUrl } from "../../../config";
import { elakayAutoText } from "../dataElakay";
import { BundleOfferCard, BundleDetailsModal, money, productThumb } from "./ProductBundleOffer";
import "./ProductBundles.css";

const MAX_PRODUCTS = 6;
const MAX_QTY = 10;
const MAX_PROMO_IMAGES = 2;
const MAX_IMAGE_BYTES = Math.floor(0.3 * 1024 * 1024);
const T = elakayAutoText;

const ISSUE_TEXT = {
  PRODUCT_NOT_FOUND: () => T("h_bnd_i_notfound", "A product no longer exists."),
  PRODUCT_DELETED: () => T("h_bnd_i_deleted", "A product was deleted."),
  PRODUCT_NOT_ACTIVE: () => T("h_bnd_i_inactive", "A product is not published for sale."),
  PRODUCT_INCOMPLETE: () => T("h_bnd_i_incomplete", "A product is incomplete."),
  BUNDLES_DISABLED_BY_OWNER: () => T("h_bnd_i_disabled", "Product combinations are disabled for a product."),
  NOT_SHARED_WITH_OTHER_SELLERS: () => T("h_bnd_i_notshared", "A product owner no longer allows other sellers to include it."),
  VARIANT_UNAVAILABLE: () => T("h_bnd_i_varunav", "A selected variant is no longer available."),
  VARIANT_OUT_OF_STOCK: () => T("h_bnd_i_varstock", "A selected variant is out of stock."),
  OUT_OF_STOCK: () => T("h_bnd_i_stock", "A product is out of stock."),
  DISCOUNT_EXCEEDS_OWNER_LIMIT: () => T("h_bnd_i_overlimit", "A discount exceeds the maximum authorized by the product owner."),
  DISCOUNT_NOT_PERMITTED: () => T("h_bnd_i_notpermitted", "A product owner does not allow discounts on their product."),
  GLOBAL_DISCOUNT_EXCEEDS_PERMISSIONS: () => T("h_bnd_i_global", "The global discount cannot be allocated within the product owners' limits."),
  NO_DISCOUNTABLE_PRODUCT: () => T("h_bnd_i_nodisc", "None of the selected products can receive a discount."),
  INVALID_DISCOUNT_VALUE: () => T("h_bnd_i_invalidvalue", "Enter a valid discount value."),
  BLOCKED_DROPSHIPPING_LOSS: () => T("h_bnd_i_dsloss", "This discount cannot be applied because it would result in a loss on a dropshipping product."),
  BLOCKED_COST_UNVERIFIABLE: () => T("h_bnd_i_costunknown", "This discount cannot be applied because the product's acquisition cost cannot be verified."),
  BLOCKED_OWNER_NO_LIQUIDATION: () => T("h_bnd_i_noliquidation", "This discount would create a loss for the product owner, who does not accept liquidation requests."),
  REQUIRES_OWNER_AUTHORIZATION: () => T("h_bnd_i_ownerauth", "Additional authorization required from the product owner."),
  REQUIRES_LOSS_CONFIRMATION: () => T("h_bnd_i_lossconfirm", "This offer results in a loss on your own inventory and requires your confirmation."),
  OWNER_PERMISSIONS_CHANGED: () => T("h_bnd_i_permchanged", "A product owner changed their bundle permissions."),
  PRODUCT_CHANGED: () => T("h_bnd_i_prodchanged", "A product in this offer changed."),
  PROVIDER_PRICING_CHANGED: () => T("h_bnd_i_providerchanged", "A provider changed its pricing."),
  LOSS_AUTHORIZATION_REVOKED: () => T("h_bnd_i_revoked", "A product owner revoked their loss authorization."),
  LOSS_AUTHORIZATION_REJECTED: () => T("h_bnd_i_rejected", "A product owner rejected the loss authorization."),
};
const issueText = (code) => (ISSUE_TEXT[code] ? ISSUE_TEXT[code]() : code);

const statusMeta = (bundle, state) => {
  if (bundle?.status === "active") return { label: T("h_bnd_s_active", "Active"), tone: "success" };
  if (bundle?.status === "paused") return { label: T("h_bnd_s_paused", "Paused"), tone: "warning" };
  if (state === "READY") return { label: T("h_bnd_s_ready", "Ready to Publish"), tone: "info" };
  if (state === "REQUIRES_OWNER_AUTHORIZATION") return { label: T("h_bnd_s_ownerauth", "Requires Owner Authorization"), tone: "warning" };
  if (state === "REQUIRES_LOSS_CONFIRMATION") return { label: T("h_bnd_s_lossconfirm", "Requires Loss Confirmation"), tone: "warning" };
  if (state === "BLOCKED_BY_FINANCIAL_PROTECTION") return { label: T("h_bnd_s_blocked", "Blocked by Financial Protection"), tone: "danger" };
  if (state === "INVALID") return { label: T("h_bnd_s_invalid", "Needs Attention"), tone: "danger" };
  return { label: T("h_bnd_s_draft", "Draft"), tone: "neutral" };
};
const financialMeta = (status) => ({
  PROFITABLE: { label: T("h_bnd_f_profit", "Profitable"), tone: "success" },
  BREAK_EVEN: { label: T("h_bnd_f_even", "Break Even"), tone: "info" },
  LOSS: { label: T("h_bnd_f_loss", "Loss Detected"), tone: "danger" },
}[status] || { label: T("h_bnd_f_unknown", "Not simulated"), tone: "neutral" });
const kindLabel = (kind) => ({
  dropshipping_external: T("h_bnd_k_external", "Dropshipping"),
  dropshipping_internal: T("h_bnd_k_internal", "Provider dropshipping"),
  provider_stock: T("h_bnd_k_providerstock", "Provider-sourced stock"),
}[kind] || null);
const Badge = ({ tone = "neutral", children }) => <span className={`pb-badge pb-badge--${tone}`}>{children}</span>;
const effectivePrice = (p) => (Number(p?.price) || 0) * (100 - (Number(p?.salePrice) || 0)) / 100;
const variantLabel = (v) => Object.values(v?.filter || {}).join(" / ") || T("h_bnd_default_option", "Default option");

// Accessible switch (a button, so the global mdb checkbox rule never applies).
const Switch = ({ checked, onChange, disabled, label, description }) => (
  <div className={`pb-switch-row${disabled ? " pb-switch-row--off" : ""}`}>
    <button type="button" role="switch" aria-checked={checked} className={`pb-switch${checked ? " pb-switch--on" : ""}`} disabled={disabled} onClick={() => onChange(!checked)} aria-label={label}>
      <span className="pb-switch-knob" />
    </button>
    <div><div className="pb-switch-label">{label}</div>{description && <div className="pb-switch-desc">{description}</div>}</div>
  </div>
);

// Public-card shape built from the editor's own simulation (for the preview).
const toPublicShape = ({ id, name, description, promotionalImages, evaluation }) => ({
  _id: id || "preview", name: name || T("h_bnd_untitled", "Untitled bundle"), description, promotionalImages,
  purchasable: false,
  totals: {
    originalTotal: evaluation.totals.originalTotal, finalTotal: evaluation.totals.finalTotal, savings: evaluation.totals.discountTotal,
    savingsPercent: evaluation.totals.savingsPercent, hasDiscount: evaluation.totals.discountTotal > 0, productCount: evaluation.totals.productCount,
  },
  lines: evaluation.lines.map((l) => ({
    productId: l.productId, quantity: l.quantity, shopName: l.shopName, product: l.product,
    regularUnitPrice: l.regularUnitPrice, unitDiscount: l.unitDiscount, finalUnitPrice: l.finalUnitPrice,
    available: true, variantChoice: false, variants: [],
  })),
});

// ── Owner permissions for the edited product ─────────────────────────────
const SettingsPanel = ({ token, productId, settings: initial, onSaved }) => {
  const [s, setS] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  // Resync with the server values after a reload, but never on mount: the
  // mount-time run could overwrite an interaction made before it flushed.
  const mountedRef = useRef(false);
  useEffect(() => {
    if (!mountedRef.current) { mountedRef.current = true; return; }
    setS(initial);
  }, [initial]);
  const set = (patch) => setS((cur) => ({ ...cur, ...patch }));
  const setLimit = (key, value) => setS((cur) => ({ ...cur, discountLimits: { ...cur.discountLimits, [key]: value } }));
  const save = async () => {
    setSaving(true); setMsg(null);
    const r = await updateProductBundleSettings(token, productId, {
      enabled: s.enabled, allowOtherSellers: s.allowOtherSellers, allowDiscountsByOtherSellers: s.allowDiscountsByOtherSellers,
      allowLossLiquidation: s.allowLossLiquidation,
      discountLimits: { maxPercentage: Number(s.discountLimits?.maxPercentage) || 0, maxFixedAmount: Number(s.discountLimits?.maxFixedAmount) || 0 },
    });
    setSaving(false);
    if (r?.success) {
      const paused = (r.revalidation || []).filter((x) => x.paused).length;
      setMsg({ type: "success", text: paused ? T("h_bnd_perm_saved_paused", "Permissions saved. {n} offer(s) no longer compliant were paused.", { n: paused }) : T("h_bnd_perm_saved", "Permissions saved.") });
      onSaved && onSaved(r.settings);
    } else setMsg({ type: "error", text: r?.error });
  };
  const noLimit = !(Number(s.discountLimits?.maxPercentage) > 0) && !(Number(s.discountLimits?.maxFixedAmount) > 0);
  return (
    <div className="pb-panel">
      <div className="pb-panel-head"><MdLock /><div><h4>{T("h_bnd_perm_title", "Product owner permissions")}</h4><p>{T("h_bnd_perm_sub", "Only you, as this product's owner, can change these settings.")}</p></div></div>
      <Switch checked={Boolean(s.enabled)} onChange={(v) => set({ enabled: v, ...(v ? {} : { allowOtherSellers: false, allowDiscountsByOtherSellers: false, allowLossLiquidation: false }) })}
        label={T("h_bnd_sw1", "Allow Product Combinations")} description={T("h_bnd_sw1_d", "Allow this product to participate in combined product offers.")} />
      <Switch checked={Boolean(s.allowOtherSellers)} disabled={!s.enabled} onChange={(v) => set({ allowOtherSellers: v, ...(v ? {} : { allowDiscountsByOtherSellers: false, allowLossLiquidation: false }) })}
        label={T("h_bnd_sw2", "Allow Other Sellers to Include This Product")} description={T("h_bnd_sw2_d", "Allow other Elakay sellers to include this product in their combined offers.")} />
      <Switch checked={Boolean(s.allowDiscountsByOtherSellers)} disabled={!s.allowOtherSellers} onChange={(v) => set({ allowDiscountsByOtherSellers: v })}
        label={T("h_bnd_sw3", "Allow Discounts in Collaborative Bundles")} description={T("h_bnd_sw3_d", "Allow other sellers to apply discounts to this product within the limits you define.")} />
      {s.allowDiscountsByOtherSellers && (
        <div className="pb-limits">
          <label className="pb-field"><span>{T("h_bnd_maxpct", "Maximum discount percentage (%)")}</span>
            <input type="number" min="0" max="99" step="0.5" value={s.discountLimits?.maxPercentage ?? 0} onChange={(e) => setLimit("maxPercentage", e.target.value)} /></label>
          <label className="pb-field"><span>{T("h_bnd_maxfixed", "Maximum fixed discount ($ per unit)")}</span>
            <input type="number" min="0" step="0.01" value={s.discountLimits?.maxFixedAmount ?? 0} onChange={(e) => setLimit("maxFixedAmount", e.target.value)} /></label>
          <p className="pb-note">{noLimit
            ? T("h_bnd_nolimit", "No limit set (0 = not set): other sellers may discount this product, but it can never be sold at a loss without your approval.")
            : T("h_bnd_lowestlimit", "When both limits are set, the lowest one applies.")}</p>
        </div>
      )}
      <Switch checked={Boolean(s.allowLossLiquidation)} disabled={!s.allowOtherSellers} onChange={(v) => set({ allowLossLiquidation: v })}
        label={T("h_bnd_sw4", "Accept Liquidation Requests")} description={T("h_bnd_sw4_d", "Let other sellers ask you to approve a specific loss-making offer on this owned inventory. Each request needs your password. Never applies to dropshipping products.")} />
      <div className="pb-row-end">
        {msg && <span className={`pb-inline-msg pb-inline-msg--${msg.type}`}>{msg.text}</span>}
        <button type="button" className="pb-btn pb-btn--primary" onClick={save} disabled={saving}>{saving ? T("h_bnd_saving", "Saving...") : T("h_bnd_save_perm", "Save permissions")}</button>
      </div>
    </div>
  );
};

// ── Liquidation / owner loss confirmation ────────────────────────────────
export const LossConfirmationModal = ({ show, onHide, token, bundleId, bundleName, line, ownerLabel, onApproved }) => {
  const [acknowledged, setAcknowledged] = useState(false);
  const [password, setPassword] = useState("");
  const [days, setDays] = useState(30);
  const [maxUnits, setMaxUnits] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  useEffect(() => { if (show) { setAcknowledged(false); setPassword(""); setError(null); } }, [show, line?.productId]);
  if (!line) return null;
  const f = line.financial || {};
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError(null);
    const pw = password;
    setPassword(""); // never kept in state beyond this single request
    const r = await approveBundleLoss(token, bundleId, line.productId, { password: pw, acknowledged, expiresInDays: Number(days), maxUnits: maxUnits === "" ? null : Number(maxUnits) });
    setBusy(false);
    if (r?.success) onApproved && onApproved(r);
    else setError(r?.error || T("h_bnd_confirm_failed", elakayAutoText("h_5db491c26288d1", "The confirmation failed.")));
  };
  const estLoss = Math.max(0, -(Number(f.netProfit) || 0));
  return (
    <Modal show={show} onHide={onHide} centered contentClassName="pb-modal" aria-labelledby="pb-loss-title">
      <form onSubmit={submit}>
        <Modal.Header closeButton><Modal.Title id="pb-loss-title" className="pb-modal-title"><MdGavel /> {T("h_bnd_loss_title", "Confirm a liquidation loss")}</Modal.Title></Modal.Header>
        <Modal.Body>
          <p className="pb-note">{T("h_bnd_loss_intro", "This offer sells your own inventory below its total cost. It will only apply to this bundle, this product and these price conditions.")}</p>
          <dl className="pb-dl">
            <dt>{T("h_bnd_bundle", "Bundle")}</dt><dd>{bundleName}</dd>
            <dt>{T("h_bnd_product", "Product")}</dt><dd>{line.product?.name}{line.quantity > 1 ? ` × ${line.quantity}` : ""}</dd>
            <dt>{T("h_bnd_orig_price", "Original price")}</dt><dd>{money(line.regularUnitPrice * line.quantity)}</dd>
            <dt>{T("h_bnd_discount", "Discount")}</dt><dd>-{money(line.unitDiscount * line.quantity)}</dd>
            <dt>{T("h_bnd_final_price", "Final selling price")}</dt><dd>{money(line.finalUnitPrice * line.quantity)}</dd>
            <dt>{T("h_bnd_total_costs", "Estimated total costs")}</dt><dd>{money(f.totalCosts)}</dd>
            <dt>{T("h_bnd_est_loss", "Estimated loss")}</dt><dd className="pb-neg">-{money(estLoss)}</dd>
            <dt>{T("h_bnd_responsible", "Seller accepting responsibility")}</dt><dd>{ownerLabel}</dd>
          </dl>
          <label className="pb-check">
            <input type="checkbox" className="pb-checkbox" checked={acknowledged} onChange={(e) => setAcknowledged(e.target.checked)} />
            <span>{T("h_bnd_loss_ack", "I understand that this offer will result in a financial loss, and I voluntarily accept responsibility for this loss as the product owner.")}</span>
          </label>
          <div className="pb-grid-2">
            <label className="pb-field"><span>{T("h_bnd_validity", "Authorization valid for")}</span>
              <select className="pb-select" value={days} onChange={(e) => setDays(e.target.value)}>
                {[7, 30, 90, 180].map((d) => <option key={d} value={d}>{T("h_bnd_days", "{n} days", { n: d })}</option>)}
              </select></label>
            <label className="pb-field"><span>{T("h_bnd_max_units", "Maximum units (optional)")}</span>
              <input type="number" min="1" step="1" value={maxUnits} onChange={(e) => setMaxUnits(e.target.value)} placeholder={T("h_bnd_unlimited", "Unlimited")} /></label>
          </div>
          <label className="pb-field"><span>{T("h_bnd_pw", "Confirm with your Elakay password")}</span>
            <input type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required /></label>
          {error && <div className="pb-alert pb-alert--error" role="alert">{error}</div>}
        </Modal.Body>
        <Modal.Footer>
          <button type="button" className="pb-btn pb-btn--ghost" onClick={onHide}>{T("h_bnd_cancel", "Cancel")}</button>
          <button type="submit" className="pb-btn pb-btn--danger" disabled={!acknowledged || !password || busy}>{busy ? T("h_bnd_confirming", "Confirming...") : T("h_bnd_confirm_loss", "Accept loss and confirm")}</button>
        </Modal.Footer>
      </form>
    </Modal>
  );
};

// ── Financial simulation (the viewer's own products only) ────────────────
const FinancialSimulation = ({ evaluation }) => {
  if (!evaluation) return <div className="pb-empty">{T("h_bnd_sim_empty", "Select at least two products to see the financial simulation.")}</div>;
  const vf = evaluation.viewerFinancial;
  const meta = financialMeta(vf.status);
  const mine = evaluation.lines.filter((l) => l.mine && l.financial?.complete);
  return (
    <div className="pb-sim">
      <div className="pb-sim-grid">
        <div><span>{T("h_bnd_orig_price", "Original price")}</span><strong>{money(evaluation.totals.originalTotal)}</strong></div>
        <div><span>{T("h_bnd_total_discount", "Total discount")}</span><strong className="pb-neg">-{money(evaluation.totals.discountTotal)}</strong></div>
        <div><span>{T("h_bnd_final_bundle", "Final bundle price")}</span><strong>{money(evaluation.totals.finalTotal)}</strong></div>
        <div><span>{T("h_bnd_est_costs", "Estimated costs")}</span><strong>{money(vf.costs)}</strong></div>
        <div><span>{T("h_bnd_est_fees", "Estimated fees")}</span><strong>{money(vf.fees)}</strong></div>
        <div><span>{T("h_bnd_net", "Estimated net profit")}</span><strong className={vf.netProfit < 0 ? "pb-neg" : "pb-pos"}>{money(vf.netProfit)}</strong></div>
        <div><span>{T("h_bnd_margin", "Profit margin")}</span><strong>{vf.marginPercent}%</strong></div>
        <div><span>{T("h_bnd_status", "Status")}</span><Badge tone={meta.tone}>{meta.label}</Badge></div>
      </div>
      {mine.length > 0 && (
        <div className="pb-table-wrap">
          <table className="pb-table">
            <thead><tr>
              <th>{T("h_bnd_product", "Product")}</th><th>{T("h_bnd_selling", "Selling price")}</th><th>{T("h_bnd_cost", "Product cost")}</th>
              <th>{T("h_bnd_logistics", "Logistics & shipping")}</th><th>{T("h_bnd_fees", "Elakay + payment fees")}</th><th>{T("h_bnd_net_short", "Net")}</th>
            </tr></thead>
            <tbody>{mine.map((l) => (
              <tr key={l.productId}>
                <td>{l.product?.name}{l.quantity > 1 ? ` × ${l.quantity}` : ""}{l.financial.costTracked === false && <div className="pb-note">{T("h_bnd_cost_untracked", "No cost price set — simulated at $0 cost.")}</div>}</td>
                <td>{money(l.financial.sellingPrice)}</td><td>{money(l.financial.productCost)}</td>
                <td>{money((l.financial.logisticsCost || 0) + (l.financial.sellerShippingCost || 0) + (l.financial.otherCosts || 0))}</td>
                <td>{money((l.financial.elakayFees || 0) + (l.financial.paymentFees || 0))}</td>
                <td className={l.financial.netProfit < 0 ? "pb-neg" : "pb-pos"}>{money(l.financial.netProfit)}</td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}
      {evaluation.lines.some((l) => !l.mine) && <p className="pb-note">{T("h_bnd_private", "Other sellers' costs are private. Each of their products is checked individually and shown with its own status only.")}</p>}
    </div>
  );
};

// ── Product selector ─────────────────────────────────────────────────────
const ProductSelector = ({ token, excludeId, selectedIds, full, onAdd }) => {
  const [scope, setScope] = useState("mine");
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [enabling, setEnabling] = useState(null);
  const abortRef = useRef(null);
  const load = useCallback(() => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);
    searchBundleProducts(token, { scope, q, exclude: excludeId }, controller.signal).then((r) => {
      if (r?.success) setItems(r.items || []);
    }).catch(() => {}).finally(() => { if (abortRef.current === controller) setLoading(false); });
  }, [token, scope, q, excludeId]);
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [load]);
  const enable = async (p) => {
    setEnabling(p._id);
    const r = await updateProductBundleSettings(token, p._id, { enabled: true });
    setEnabling(null);
    if (r?.success) setItems((cur) => cur.map((x) => (x._id === p._id ? { ...x, bundle: { ...x.bundle, enabled: true, eligible: true } } : x)));
  };
  return (
    <div className="pb-selector">
      <div className="pb-tabs" role="tablist">
        <button type="button" role="tab" aria-selected={scope === "mine"} className={`pb-tab${scope === "mine" ? " pb-tab--on" : ""}`} onClick={() => setScope("mine")}>{T("h_bnd_my_products", "My Products")}</button>
        <button type="button" role="tab" aria-selected={scope === "others"} className={`pb-tab${scope === "others" ? " pb-tab--on" : ""}`} onClick={() => setScope("others")}>{T("h_bnd_other_products", "Other Sellers' Products")}</button>
      </div>
      <div className="pb-search"><MdSearch /><input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={T("h_bnd_search", "Search products by name...")} aria-label={T("h_bnd_search", "Search products by name...")} /></div>
      {scope === "others" && <p className="pb-note">{T("h_bnd_others_note", "Only products whose owners explicitly allow collaborative offers are listed.")}</p>}
      <div className="pb-results" aria-busy={loading}>
        {loading && !items.length && <div className="pb-empty">{T("h_bnd_loading", "Loading...")}</div>}
        {!loading && !items.length && <div className="pb-empty">{T("h_bnd_no_results", "No eligible products found.")}</div>}
        {items.map((p) => {
          const selected = selectedIds.includes(p._id);
          const kl = kindLabel(p.kind);
          return (
            <div key={p._id} className="pb-result">
              <img src={productThumb(p)} alt="" />
              <div className="pb-result-body">
                <div className="pb-result-name">{p.name}</div>
                <div className="pb-result-meta">
                  <span><MdStorefront /> {p.shopName}</span>
                  <span>{money(effectivePrice(p))}</span>
                  <span>{T("h_bnd_in_stock", "{n} in stock", { n: p.stock })}</span>
                  {p.variants?.length > 1 && <span>{T("h_bnd_n_variants", "{n} variants", { n: p.variants.length })}{p.variantKeys?.length ? ` (${p.variantKeys.join(", ")})` : ""}</span>}
                </div>
                <div className="pb-result-badges">
                  <Badge tone={p.mine ? "info" : "neutral"}>{p.mine ? T("h_bnd_mine", "My product") : T("h_bnd_other", "Other seller")}</Badge>
                  <Badge tone="success">{T("h_bnd_published", "Published")}</Badge>
                  {kl && <Badge tone="warning">{kl}</Badge>}
                  {!p.mine && (p.bundle.allowDiscount
                    ? <Badge tone="info">{T("h_bnd_disc_allowed", "Discount allowed")}{p.bundle.maxPercentage ? ` ≤ ${p.bundle.maxPercentage}%` : ""}{p.bundle.maxFixedAmount ? ` ≤ ${money(p.bundle.maxFixedAmount)}` : ""}</Badge>
                    : <Badge tone="neutral">{T("h_bnd_no_disc", "Fixed price (no discount)")}</Badge>)}
                  {!p.bundle.eligible && <Badge tone="danger">{T("h_bnd_not_enabled", "Combinations disabled")}</Badge>}
                </div>
              </div>
              {p.mine && !p.bundle.enabled
                ? <button type="button" className="pb-btn pb-btn--ghost pb-btn--sm" onClick={() => enable(p)} disabled={enabling === p._id}>{T("h_bnd_enable", "Enable")}</button>
                : <button type="button" className="pb-btn pb-btn--primary pb-btn--sm" onClick={() => onAdd(p)} disabled={selected || full || !p.bundle.eligible} aria-label={`${T("h_bnd_add", "Add")} ${p.name}`}>
                  {selected ? T("h_bnd_added_short", "Added") : <><MdAdd /> {T("h_bnd_add", "Add")}</>}
                </button>}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ── Bundle editor ────────────────────────────────────────────────────────
const METHODS = [
  { id: "none", label: () => T("h_bnd_m_none", "No discount"), hint: () => T("h_bnd_m_none_h", "Sum of the selected products.") },
  { id: "global_percent", label: () => T("h_bnd_m_gpct", "Global percentage"), hint: () => T("h_bnd_m_gpct_h", "One percentage on every product that allows it.") },
  { id: "global_fixed", label: () => T("h_bnd_m_gfix", "Global fixed amount"), hint: () => T("h_bnd_m_gfix_h", "One amount, split across eligible products.") },
  { id: "individual_percent", label: () => T("h_bnd_m_ipct", "Individual percentages"), hint: () => T("h_bnd_m_ipct_h", "A percentage per product.") },
  { id: "individual_fixed", label: () => T("h_bnd_m_ifix", "Individual fixed amounts"), hint: () => T("h_bnd_m_ifix_h", "An amount per product unit.") },
];

const BundleEditor = ({ token, primary, row, ownerLabel, onClose, onSaved }) => {
  const bundle = row?.bundle || null;
  const [bundleId, setBundleId] = useState(bundle?._id || null);
  const [name, setName] = useState(bundle?.name || "");
  const [description, setDescription] = useState(bundle?.description || "");
  const [lines, setLines] = useState(() => (row?.evaluation?.lines || []).length
    ? row.evaluation.lines.map((l) => ({ productId: l.productId, product: l.product, quantity: l.quantity, filterProduct: l.filterProduct, mine: l.mine, shopName: l.shopName, variants: null }))
    : [{ productId: primary._id, product: primary, quantity: 1, filterProduct: null, mine: true, shopName: primary.shopName, variants: primary.variants }]);
  const [method, setMethod] = useState(bundle?.pricing?.discountMethod || "none");
  const [value, setValue] = useState(bundle?.pricing?.discountValue || "");
  const [lineDiscounts, setLineDiscounts] = useState(() => Object.fromEntries((row?.evaluation?.lines || []).filter((l) => l.requestedValue > 0).map((l) => [l.productId, l.requestedValue])));
  const [promoImages, setPromoImages] = useState(bundle?.promotionalImages || []);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [evaluation, setEvaluation] = useState(row?.evaluation || null);
  const [pricingIssues, setPricingIssues] = useState([]);
  const [simError, setSimError] = useState(null);
  const [simulating, setSimulating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [lossLine, setLossLine] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const abortRef = useRef(null);
  const variantCache = useRef(new Map(primary?.variants ? [[primary._id, primary.variants]] : []));

  const payload = useMemo(() => ({
    bundleId: bundleId || undefined,
    name: name.trim() || T("h_bnd_untitled", "Untitled bundle"), description,
    primaryProduct: primary._id,
    products: lines.map((l) => ({ product: l.productId, quantity: l.quantity, filterProduct: l.filterProduct || null })),
    discountMethod: method,
    discountValue: method.startsWith("global_") ? Number(value) || 0 : 0,
    lineDiscounts: method.startsWith("individual_") ? Object.fromEntries(lines.map((l) => [l.productId, Number(lineDiscounts[l.productId]) || 0])) : undefined,
  }), [bundleId, name, description, primary._id, lines, method, value, lineDiscounts]);

  // Live server simulation (debounced, previous request aborted).
  useEffect(() => {
    if (lines.length < 2) { setEvaluation(null); setPricingIssues([]); setSimError(null); return undefined; }
    const t = setTimeout(() => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      setSimulating(true);
      simulateProductBundle(token, payload, controller.signal).then((r) => {
        if (r?.success) { setEvaluation(r.evaluation); setPricingIssues(r.pricingIssues || []); setSimError(null); }
        else if (r) { setSimError(r.error); setEvaluation(null); }
      }).catch(() => {}).finally(() => { if (abortRef.current === controller) setSimulating(false); });
    }, 450);
    return () => clearTimeout(t);
  }, [payload, token, lines.length]);

  const evLine = (pid) => evaluation?.lines.find((l) => l.productId === pid);
  const addLine = (p) => {
    if (lines.length >= MAX_PRODUCTS || lines.some((l) => l.productId === p._id)) return;
    variantCache.current.set(p._id, p.variants || []);
    setLines((cur) => [...cur, { productId: p._id, product: p, quantity: 1, filterProduct: null, mine: p.mine, shopName: p.shopName, variants: p.variants || [] }]);
  };
  const updateLine = (pid, patch) => setLines((cur) => cur.map((l) => (l.productId === pid ? { ...l, ...patch } : l)));
  const removeLine = (pid) => setLines((cur) => cur.filter((l) => l.productId !== pid || pid === primary._id));

  const pickFiles = (fileList) => {
    const files = Array.from(fileList || []);
    const room = MAX_PROMO_IMAGES - promoImages.length - pendingFiles.length;
    if (files.length > room) { setMessage({ type: "error", text: T("h_bnd_max_images", elakayAutoText("h_89add5b75ae499", "You can add at most 2 promotional images.")) }); return; }
    const bad = files.find((f) => !f.type.startsWith("image/") || f.size > MAX_IMAGE_BYTES);
    if (bad) { setMessage({ type: "error", text: T("h_bnd_image_invalid", elakayAutoText("h_71b7ccf169a74a", "Images must be image files of 0.3 MB maximum.")) }); return; }
    setMessage(null);
    setPendingFiles((cur) => [...cur, ...files]);
  };
  const pendingUrls = useMemo(() => pendingFiles.map((f) => (typeof URL !== "undefined" && URL.createObjectURL ? URL.createObjectURL(f) : "")), [pendingFiles]);
  useEffect(() => () => pendingUrls.forEach((u) => u && URL.revokeObjectURL && URL.revokeObjectURL(u)), [pendingUrls]);

  const uploadPending = async (id) => {
    let latest = null;
    for (const f of pendingFiles) {
      const r = await uploadProductBundleImage(token, id, f);
      if (!r?.success) { setMessage({ type: "error", text: r?.error }); break; }
      latest = r.bundle;
    }
    setPendingFiles([]);
    if (latest) setPromoImages(latest.promotionalImages);
  };
  const removeImage = async (img) => {
    const r = await removeProductBundleImage(token, bundleId, img._id);
    if (r?.success) setPromoImages(r.bundle.promotionalImages);
  };

  const persist = async (publish) => {
    if (!name.trim()) { setMessage({ type: "error", text: T("h_bnd_name_required", elakayAutoText("h_e720bbad123811", "Enter a bundle name.")) }); return null; }
    setSaving(true); setMessage(null);
    const body = { ...payload, name: name.trim(), publish };
    const r = bundleId ? await updateProductBundle(token, bundleId, body) : await createProductBundle(token, body);
    setSaving(false);
    if (!r?.success) { setMessage({ type: "error", text: r?.error }); return null; }
    setBundleId(r.bundle._id);
    setEvaluation(r.evaluation);
    if (pendingFiles.length) await uploadPending(r.bundle._id);
    onSaved && onSaved();
    return r;
  };
  const saveDraft = async () => { if (await persist(false)) setMessage({ type: "success", text: T("h_bnd_saved", elakayAutoText("h_a70e35ce2bea5d", "Bundle saved.")) }); };
  const publish = async () => {
    const state = evaluation?.state;
    if (state === "READY") {
      if (await persist(true)) setMessage({ type: "success", text: T("h_bnd_published_ok", elakayAutoText("h_b55aebb5737200", "Bundle published.")) });
      return;
    }
    const saved = await persist(false);
    if (!saved) return;
    const ev = saved.evaluation;
    if (ev.state === "REQUIRES_LOSS_CONFIRMATION") setLossLine(ev.lines.find((l) => l.mine && l.decision === "REQUIRES_LOSS_CONFIRMATION"));
    else if (ev.state === "REQUIRES_OWNER_AUTHORIZATION") setMessage({ type: "warning", text: issueText("REQUIRES_OWNER_AUTHORIZATION") });
  };
  const afterLossApproved = async (r) => {
    setLossLine(null);
    setEvaluation(r.evaluation);
    if (r.evaluation.state === "READY") {
      const s = await setProductBundleStatus(token, bundleId, "active");
      setMessage(s?.success ? { type: "success", text: T("h_bnd_published_ok", elakayAutoText("h_b55aebb5737200", "Bundle published.")) } : { type: "error", text: s?.error });
    } else {
      const next = r.evaluation.lines.find((l) => l.mine && l.decision === "REQUIRES_LOSS_CONFIRMATION");
      if (next) setLossLine(next);
    }
    onSaved && onSaved();
  };
  const requestAuth = async (pid) => {
    const r = await requestBundleOwnerAuthorization(token, bundleId, pid);
    setMessage(r?.success ? { type: "success", text: T("h_bnd_auth_requested", elakayAutoText("h_ecb2b17896c43b", "Authorization request sent to the product owner.")) } : { type: "error", text: r?.error });
    onSaved && onSaved();
  };

  const state = evaluation?.state;
  const blocked = !evaluation || ["INVALID", "BLOCKED_BY_FINANCIAL_PROTECTION"].includes(state) || pricingIssues.length > 0 || Boolean(simError);
  const pendingRequestFor = (pid) => (row?.bundle?.ownerAuthorizations || []).find((a) => a.product === pid && a.status === "pending");
  const promoForPreview = [...promoImages.map((i) => i.url), ...pendingUrls].filter(Boolean);
  const meta = statusMeta({ status: bundle?.status }, state);

  return (
    <div className="pb-editor">
      <div className="pb-editor-head">
        <div>
          <h4>{bundleId ? T("h_bnd_edit_title", "Edit bundle") : T("h_bnd_new_title", "New bundle")}</h4>
          <div className="pb-editor-status"><Badge tone={meta.tone}>{meta.label}</Badge>{simulating && <span className="pb-note">{T("h_bnd_simulating", "Recalculating...")}</span>}</div>
        </div>
        <button type="button" className="pb-icon-btn" onClick={onClose} aria-label={T("h_bnd_close", "Close")}><MdClose /></button>
      </div>

      <section className="pb-section">
        <h5>1. {T("h_bnd_sec_products", "Product Selection")}</h5>
        <p className="pb-note">{T("h_bnd_sec_products_h", "Select 2 to 6 eligible products. This product is included automatically.")}</p>
        <ul className="pb-lines">
          {lines.map((l) => {
            const ev = evLine(l.productId);
            const variants = l.variants || l.product?.variants || variantCache.current.get(l.productId) || [];
            const kl = kindLabel(ev?.kind);
            return (
              <li key={l.productId} className="pb-line">
                <img src={productThumb(l.product)} alt="" />
                <div className="pb-line-body">
                  <div className="pb-line-name">{l.product?.name}{l.productId === primary._id && <Badge tone="info">{T("h_bnd_primary", "Primary")}</Badge>}</div>
                  <div className="pb-result-meta">
                    <span><MdStorefront /> {l.shopName}</span>
                    <Badge tone={l.mine ? "info" : "neutral"}>{l.mine ? T("h_bnd_mine", "My product") : T("h_bnd_other", "Other seller")}</Badge>
                    {kl && <Badge tone="warning">{kl}</Badge>}
                  </div>
                  <div className="pb-line-controls">
                    <label className="pb-field pb-field--inline"><span>{T("h_bnd_qty", "Qty")}</span>
                      <input type="number" min="1" max={MAX_QTY} value={l.quantity} onChange={(e) => updateLine(l.productId, { quantity: Math.min(MAX_QTY, Math.max(1, parseInt(e.target.value, 10) || 1)) })} /></label>
                    {variants.length > 1 && (
                      <label className="pb-field pb-field--inline"><span>{T("h_bnd_variant", "Variant")}</span>
                        <select className="pb-select" value={l.filterProduct || ""} onChange={(e) => updateLine(l.productId, { filterProduct: e.target.value || null })}>
                          <option value="">{T("h_bnd_customer_chooses", "Customer chooses")}</option>
                          {variants.map((v) => <option key={v._id} value={v._id}>{variantLabel(v)}</option>)}
                        </select></label>
                    )}
                    {method.startsWith("individual_") && (
                      <label className="pb-field pb-field--inline"><span>{method === "individual_percent" ? T("h_bnd_disc_pct", "Discount %") : T("h_bnd_disc_amt", "Discount $/unit")}</span>
                        <input type="number" min="0" step={method === "individual_percent" ? "0.5" : "0.01"} value={lineDiscounts[l.productId] ?? ""} disabled={ev?.permissions && !ev.permissions.allowDiscount}
                          onChange={(e) => setLineDiscounts((cur) => ({ ...cur, [l.productId]: e.target.value }))} /></label>
                    )}
                  </div>
                  {ev?.permissions && !l.mine && (
                    <div className="pb-note">{ev.permissions.allowDiscount
                      ? T("h_bnd_owner_max", "Owner allows up to {amt} off per unit.", { amt: money(ev.permissions.maxDiscountPerUnit) })
                      : T("h_bnd_owner_fixed", "Owner requires the authorized selling price (no discount).")}</div>
                  )}
                  {ev && (
                    <div className="pb-line-result">
                      <span>{ev.unitDiscount > 0 && <span className="pb-strike">{money(ev.regularUnitPrice)}</span>} <strong>{money(ev.finalUnitPrice)}</strong>{l.quantity > 1 ? ` × ${l.quantity}` : ""}</span>
                      {ev.financial && <Badge tone={financialMeta(ev.financial.status).tone}>{financialMeta(ev.financial.status).label}</Badge>}
                      {l.mine && ev.financial?.complete && <span className={ev.financial.netProfit < 0 ? "pb-neg" : "pb-pos"}>{T("h_bnd_net_short", "Net")} {money(ev.financial.netProfit)}</span>}
                    </div>
                  )}
                  {ev && ev.decision !== "OK" && ev.decision !== "AUTHORIZED_LOSS" && (
                    <div className={`pb-line-issue${ev.decision.startsWith("REQUIRES_") ? " pb-line-issue--warn" : ""}`}>
                      <MdWarning /> {ev.message || (ev.issues || []).map(issueText).join(" ")}
                      {ev.decision === "REQUIRES_OWNER_AUTHORIZATION" && bundleId && (pendingRequestFor(l.productId)
                        ? <Badge tone="warning">{T("h_bnd_request_pending", "Request pending")}</Badge>
                        : <button type="button" className="pb-btn pb-btn--ghost pb-btn--sm" onClick={() => requestAuth(l.productId)}>{T("h_bnd_request_auth", "Request owner authorization")}</button>)}
                      {ev.decision === "REQUIRES_OWNER_AUTHORIZATION" && !bundleId && <span className="pb-note"> {T("h_bnd_save_first", "Save the draft to request it.")}</span>}
                      {ev.decision === "REQUIRES_LOSS_CONFIRMATION" && bundleId && <button type="button" className="pb-btn pb-btn--ghost pb-btn--sm" onClick={() => setLossLine(ev)}>{T("h_bnd_confirm_loss_btn", "Confirm loss")}</button>}
                    </div>
                  )}
                  {ev?.decision === "AUTHORIZED_LOSS" && <div className="pb-note"><MdCheckCircle /> {T("h_bnd_loss_authorized", "Loss authorized by the product owner for this offer.")}</div>}
                </div>
                {l.productId !== primary._id && <button type="button" className="pb-icon-btn" onClick={() => removeLine(l.productId)} aria-label={`${T("h_bnd_remove", "Remove")} ${l.product?.name || ""}`}><MdClose /></button>}
              </li>
            );
          })}
        </ul>
        <div className="pb-count">{lines.length}/{MAX_PRODUCTS}</div>
        <ProductSelector token={token} excludeId={primary._id} selectedIds={lines.map((l) => l.productId)} full={lines.length >= MAX_PRODUCTS} onAdd={addLine} />
      </section>

      <section className="pb-section">
        <h5>2. {T("h_bnd_sec_info", "Bundle Information")}</h5>
        <label className="pb-field"><span>{T("h_bnd_name", "Bundle name")}</span>
          <input type="text" maxLength={120} value={name} onChange={(e) => setName(e.target.value)} placeholder={T("h_bnd_name_ph", "Complete Summer Outfit")} /></label>
        <label className="pb-field"><span>{T("h_bnd_desc", "Bundle description")}</span>
          <textarea rows={3} maxLength={1000} value={description} onChange={(e) => setDescription(e.target.value)} placeholder={T("h_bnd_desc_ph", "Describe exactly what the customer gets in this offer.")} /></label>
        <div className="pb-field"><span>{T("h_bnd_promo", "Promotional images (optional, up to 2)")}</span>
          <div className="pb-images">
            {promoImages.map((img) => (
              <div className="pb-image" key={img._id}><img src={toImageUrl(img.url)} alt="" /><button type="button" className="pb-icon-btn" onClick={() => removeImage(img)} aria-label={T("h_bnd_remove", "Remove")}><MdClose /></button></div>
            ))}
            {pendingUrls.map((u, i) => (
              <div className="pb-image pb-image--pending" key={u || i}><img src={u} alt="" /><button type="button" className="pb-icon-btn" onClick={() => setPendingFiles((cur) => cur.filter((_, j) => j !== i))} aria-label={T("h_bnd_remove", "Remove")}><MdClose /></button></div>
            ))}
            {promoImages.length + pendingFiles.length < MAX_PROMO_IMAGES && (
              <label className="pb-image-add"><MdImage /><span>{T("h_bnd_add_image", "Add image")}</span>
                <input type="file" accept="image/*" onChange={(e) => { pickFiles(e.target.files); e.target.value = ""; }} hidden /></label>
            )}
          </div>
          <p className="pb-note">{T("h_bnd_promo_note", "Product images are always shown. Extra images present the complete offer (0.3 MB max each).")}</p>
        </div>
      </section>

      <section className="pb-section">
        <h5>3. {T("h_bnd_sec_discount", "Discount Configuration")}</h5>
        <div className="pb-methods" role="radiogroup" aria-label={T("h_bnd_sec_discount", "Discount Configuration")}>
          {METHODS.map((m) => (
            <button type="button" role="radio" aria-checked={method === m.id} key={m.id} className={`pb-method${method === m.id ? " pb-method--on" : ""}`} onClick={() => setMethod(m.id)}>
              <strong>{m.label()}</strong><span>{m.hint()}</span>
            </button>
          ))}
        </div>
        {method.startsWith("global_") && (
          <label className="pb-field pb-field--narrow"><span>{method === "global_percent" ? T("h_bnd_disc_pct", "Discount %") : T("h_bnd_disc_total", "Discount amount ($)")}</span>
            <input type="number" min="0" step={method === "global_percent" ? "0.5" : "0.01"} value={value} onChange={(e) => setValue(e.target.value)} /></label>
        )}
        {pricingIssues.map((p, i) => <div key={i} className="pb-alert pb-alert--error">{issueText(p.code)}{p.maxAllocatableCents !== undefined ? ` ${T("h_bnd_max_alloc", "Maximum allowed: {amt}.", { amt: money(p.maxAllocatableCents / 100) })}` : ""}</div>)}
        <p className="pb-note">{T("h_bnd_disc_rule", "Discounts are always recorded per product. Another seller's product is only discounted within the limits its owner set.")}</p>
      </section>

      <section className="pb-section">
        <h5>4. {T("h_bnd_sec_sim", "Financial Simulation")}</h5>
        {simError ? <div className="pb-alert pb-alert--error">{simError}</div> : <FinancialSimulation evaluation={evaluation} />}
      </section>

      <section className="pb-section">
        <h5>5. {T("h_bnd_sec_preview", "Preview")}</h5>
        {evaluation ? (
          <div className="pb-preview-wrap">
            <BundleOfferCard bundle={toPublicShape({ id: bundleId, name, description, promotionalImages: promoForPreview, evaluation })} onView={() => setPreviewOpen(true)} preview />
          </div>
        ) : <div className="pb-empty">{T("h_bnd_sim_empty", "Select at least two products to see the financial simulation.")}</div>}
      </section>

      {message && <div className={`pb-alert pb-alert--${message.type}`} role="status">{message.text}</div>}
      <div className="pb-editor-actions">
        <button type="button" className="pb-btn pb-btn--ghost" onClick={onClose}>{T("h_bnd_cancel", "Cancel")}</button>
        <button type="button" className="pb-btn pb-btn--ghost" onClick={saveDraft} disabled={saving || blocked}>{saving ? T("h_bnd_saving", "Saving...") : T("h_bnd_save_draft", "Save Draft")}</button>
        <button type="button" className="pb-btn pb-btn--primary" onClick={publish} disabled={saving || blocked || simulating}>{T("h_bnd_publish", "Publish")}</button>
      </div>

      {previewOpen && evaluation && <BundleDetailsModal show preview bundle={toPublicShape({ id: bundleId, name, description, promotionalImages: promoForPreview, evaluation })} onHide={() => setPreviewOpen(false)} />}
      <LossConfirmationModal show={Boolean(lossLine)} onHide={() => setLossLine(null)} token={token} bundleId={bundleId} bundleName={name} line={lossLine} ownerLabel={ownerLabel} onApproved={afterLossApproved} />
    </div>
  );
};

// ── Bundle card in the management list ───────────────────────────────────
const BundleCard = ({ row, onEdit, onPreview, onStatus, onDelete, onDuplicate, busy }) => {
  const { bundle, evaluation } = row;
  const meta = statusMeta(bundle, evaluation.state);
  const fin = financialMeta(evaluation.viewerFinancial.status);
  const cover = bundle.promotionalImages?.[0]?.url;
  return (
    <div className="pb-card">
      <div className="pb-card-cover">
        {cover ? <img src={toImageUrl(cover)} alt="" /> : evaluation.lines.slice(0, 4).map((l) => <img key={l.productId} src={productThumb(l.product)} alt="" />)}
      </div>
      <div className="pb-card-body">
        <div className="pb-card-head"><h5>{bundle.name}</h5><Badge tone={meta.tone}>{meta.label}</Badge></div>
        <div className="pb-card-products">{evaluation.lines.map((l) => l.product?.name).filter(Boolean).join(" + ")}</div>
        <div className="pb-card-figures">
          <span>{T("h_bnd_count_short", "{n} products", { n: evaluation.totals.productCount })}</span>
          <span>{T("h_bnd_original_total", "Original total")}: {money(evaluation.totals.originalTotal)}</span>
          <span>{T("h_bnd_final", "Final")}: <strong>{money(evaluation.totals.finalTotal)}</strong></span>
          {evaluation.totals.discountTotal > 0 && <span className="pb-pos">{T("h_bnd_savings", "Savings")}: {money(evaluation.totals.discountTotal)}</span>}
          <Badge tone={fin.tone}>{fin.label}</Badge>
          <span className="pb-note">{T("h_bnd_updated", "Updated")} {new Date(bundle.updatedAt).toLocaleDateString()}</span>
        </div>
        {bundle.systemPause && bundle.status === "paused" && (
          <div className="pb-alert pb-alert--warning"><MdWarning /> {T("h_bnd_system_paused", "Paused automatically")}: {[bundle.systemPause.reason, ...(bundle.systemPause.issues || [])].filter(Boolean).map(issueText).join(" ")}</div>
        )}
        {bundle.status !== "paused" && evaluation.issues?.length > 0 && <div className="pb-note">{evaluation.issues.map(issueText).join(" ")}</div>}
        <div className="pb-card-actions">
          <button type="button" className="pb-btn pb-btn--ghost pb-btn--sm" onClick={onEdit} disabled={busy}><MdEdit /> {T("h_bnd_edit", "Edit")}</button>
          <button type="button" className="pb-btn pb-btn--ghost pb-btn--sm" onClick={onPreview} disabled={busy}><MdVisibility /> {T("h_bnd_preview", "Preview")}</button>
          {bundle.status === "active"
            ? <button type="button" className="pb-btn pb-btn--ghost pb-btn--sm" onClick={() => onStatus("paused")} disabled={busy}><MdPause /> {T("h_bnd_pause", "Pause")}</button>
            : <button type="button" className="pb-btn pb-btn--ghost pb-btn--sm" onClick={() => onStatus("active")} disabled={busy}><MdPlayArrow /> {T("h_bnd_activate", "Activate")}</button>}
          <button type="button" className="pb-btn pb-btn--ghost pb-btn--sm" onClick={onDuplicate} disabled={busy}><MdContentCopy /> {T("h_bnd_duplicate", "Duplicate")}</button>
          <button type="button" className="pb-btn pb-btn--danger-ghost pb-btn--sm" onClick={onDelete} disabled={busy}><MdDelete /> {T("h_bnd_delete", "Delete")}</button>
        </div>
      </div>
    </div>
  );
};

// Another Seller's offer that includes one of MY products: I review it.
const OwnerReviewCard = ({ row, token, ownerLabel, onChanged }) => {
  const { bundle, evaluation } = row;
  const mine = evaluation.lines.filter((l) => l.mine);
  const [approveLine, setApproveLine] = useState(null);
  const [msg, setMsg] = useState(null);
  const act = async (fn) => { const r = await fn(); setMsg(r?.success ? null : { type: "error", text: r?.error }); onChanged && onChanged(); };
  return (
    <div className="pb-card pb-card--review">
      <div className="pb-card-body">
        <div className="pb-card-head"><h5>{bundle.name}</h5><Badge tone={statusMeta(bundle, evaluation.state).tone}>{statusMeta(bundle, evaluation.state).label}</Badge></div>
        <div className="pb-card-products">{T("h_bnd_by_other", "Offer created by another seller including your product.")}</div>
        {mine.map((l) => {
          const auths = bundle.ownerAuthorizations.filter((a) => a.product === l.productId);
          const pending = auths.find((a) => a.status === "pending");
          const approved = auths.find((a) => a.status === "approved");
          return (
            <div key={l.productId} className="pb-review-line">
              <img src={productThumb(l.product)} alt="" />
              <div className="pb-line-body">
                <strong>{l.product?.name}{l.quantity > 1 ? ` × ${l.quantity}` : ""}</strong>
                <div className="pb-result-meta">
                  <span>{T("h_bnd_proposed", "Proposed discount")}: {money(l.unitDiscount)} / {T("h_bnd_unit", "unit")}</span>
                  <span>{T("h_bnd_final_price", "Final selling price")}: {money(l.finalUnitPrice)}</span>
                  {l.financial?.complete && <span>{T("h_bnd_fees", "Elakay + payment fees")}: {money((l.financial.elakayFees || 0) + (l.financial.paymentFees || 0))}</span>}
                  {l.financial?.complete && <span>{T("h_bnd_total_costs", "Estimated total costs")}: {money(l.financial.totalCosts)}</span>}
                  {l.financial?.complete && <span className={l.financial.netProfit < 0 ? "pb-neg" : "pb-pos"}>{T("h_bnd_net_short", "Net")}: {money(l.financial.netProfit)}</span>}
                </div>
                {pending && l.decision === "REQUIRES_OWNER_AUTHORIZATION" && (
                  <div className="pb-card-actions">
                    <Badge tone="warning">{T("h_bnd_auth_requested_badge", "Loss authorization requested")}</Badge>
                    <button type="button" className="pb-btn pb-btn--danger pb-btn--sm" onClick={() => setApproveLine(l)}>{T("h_bnd_review_approve", "Review & approve")}</button>
                    <button type="button" className="pb-btn pb-btn--ghost pb-btn--sm" onClick={() => act(() => rejectBundleAuthorization(token, bundle._id, pending._id))}>{T("h_bnd_reject", "Reject")}</button>
                  </div>
                )}
                {approved && (
                  <div className="pb-card-actions">
                    <Badge tone="success">{T("h_bnd_auth_approved", "Loss approved")}{approved.expiresAt ? ` · ${T("h_bnd_until", "until")} ${new Date(approved.expiresAt).toLocaleDateString()}` : ""}</Badge>
                    <button type="button" className="pb-btn pb-btn--ghost pb-btn--sm" onClick={() => act(() => revokeBundleAuthorization(token, bundle._id, approved._id))}>{T("h_bnd_revoke", "Revoke")}</button>
                  </div>
                )}
              </div>
            </div>
          );
        })}
        {msg && <div className={`pb-alert pb-alert--${msg.type}`}>{msg.text}</div>}
      </div>
      <LossConfirmationModal show={Boolean(approveLine)} onHide={() => setApproveLine(null)} token={token} bundleId={bundle._id} bundleName={bundle.name} line={approveLine} ownerLabel={ownerLabel}
        onApproved={() => { setApproveLine(null); onChanged && onChanged(); }} />
    </div>
  );
};

// ── Entry point (Product Edit → Product Bundles) ─────────────────────────
const ProductBundleManager = ({ token, productId, ownerLabel }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(null); // { row } | { row: null } for create
  const [busyId, setBusyId] = useState(null);
  const [preview, setPreview] = useState(null);
  const [lossFor, setLossFor] = useState(null);
  const [notice, setNotice] = useState(null);

  const load = useCallback(async () => {
    if (!token || !productId) return;
    const r = await listProductBundles(token, productId);
    setLoading(false);
    if (r?.success) { setData(r); setError(null); } else setError(r?.error || T("h_bnd_load_failed", elakayAutoText("h_f67d909d253e0c", "Bundles could not be loaded.")));
  }, [token, productId]);
  useEffect(() => { setLoading(true); load(); }, [load]);

  const mineRows = (data?.bundles || []).filter((r) => r.bundle.isOwner);
  const reviewRows = (data?.bundles || []).filter((r) => !r.bundle.isOwner);
  const label = ownerLabel || T("h_bnd_you_owner", "You (product owner)");

  const changeStatus = async (row, status) => {
    setBusyId(row.bundle._id); setNotice(null);
    const r = await setProductBundleStatus(token, row.bundle._id, status);
    setBusyId(null);
    if (r?.success) { load(); return; }
    if (r?.code === "REQUIRES_LOSS_CONFIRMATION") {
      const l = row.evaluation.lines.find((x) => x.mine && x.decision === "REQUIRES_LOSS_CONFIRMATION");
      if (l) { setLossFor({ row, line: l }); return; }
    }
    setNotice({ type: "error", text: r?.error });
  };
  const remove = async (row) => {
    if (!window.confirm(T("h_bnd_confirm_delete", elakayAutoText("h_bde4033cd68482", "Delete this bundle? Customers will no longer see it.")))) return;
    setBusyId(row.bundle._id);
    const r = await deleteProductBundle(token, row.bundle._id);
    setBusyId(null);
    if (!r?.success) setNotice({ type: "error", text: r?.error });
    load();
  };
  const duplicate = async (row) => {
    setBusyId(row.bundle._id);
    const r = await duplicateProductBundle(token, row.bundle._id);
    setBusyId(null);
    if (!r?.success) setNotice({ type: "error", text: r?.error });
    load();
  };

  if (loading) return <div className="pb-manager"><div className="pb-empty">{T("h_bnd_loading", "Loading...")}</div></div>;
  if (error) return <div className="pb-manager"><div className="pb-alert pb-alert--error">{error}</div></div>;

  return (
    <div className="pb-manager">
      <div className="pb-manager-head">
        <div><h3><MdLayers /> {T("h_bnd_manager_title", "Product Bundle Manager")}</h3><p>{T("h_bnd_manager_sub", "Create and manage combined product offers.")}</p></div>
        {!editing && <button type="button" className="pb-btn pb-btn--primary" onClick={() => setEditing({ row: null })} disabled={!data?.product}><MdAdd /> {T("h_bnd_create", "Create Bundle")}</button>}
      </div>
      {notice && <div className={`pb-alert pb-alert--${notice.type}`}>{notice.text}</div>}
      {data?.settings && !data.settings.enabled && !editing && (
        <div className="pb-alert pb-alert--info">{T("h_bnd_enable_hint", "Enable \"Allow Product Combinations\" below to include this product in bundles.")}</div>
      )}

      {editing ? (
        <BundleEditor token={token} primary={data.product} row={editing.row} ownerLabel={label}
          onClose={() => { setEditing(null); load(); }} onSaved={load} />
      ) : (
        <>
          <SettingsPanel token={token} productId={data.productId} settings={data.settings} onSaved={load} />
          <div className="pb-list">
            <h4>{T("h_bnd_your_bundles", "Your bundles with this product")}</h4>
            {!mineRows.length && <div className="pb-empty">{T("h_bnd_none", "No bundles yet. Create a combined offer to sell this product together with others.")}</div>}
            {mineRows.map((row) => (
              <BundleCard key={row.bundle._id} row={row} busy={busyId === row.bundle._id}
                onEdit={() => setEditing({ row })} onPreview={() => setPreview(row)}
                onStatus={(s) => changeStatus(row, s)} onDelete={() => remove(row)} onDuplicate={() => duplicate(row)} />
            ))}
          </div>
          {reviewRows.length > 0 && (
            <div className="pb-list">
              <h4>{T("h_bnd_collab", "Collaborative offers including this product")}</h4>
              {reviewRows.map((row) => <OwnerReviewCard key={row.bundle._id} row={row} token={token} ownerLabel={label} onChanged={load} />)}
            </div>
          )}
        </>
      )}

      {preview && (
        <BundleDetailsModal show preview onHide={() => setPreview(null)}
          bundle={toPublicShape({ id: preview.bundle._id, name: preview.bundle.name, description: preview.bundle.description, promotionalImages: preview.bundle.promotionalImages.map((i) => i.url), evaluation: preview.evaluation })} />
      )}
      <LossConfirmationModal show={Boolean(lossFor)} onHide={() => setLossFor(null)} token={token} bundleId={lossFor?.row.bundle._id} bundleName={lossFor?.row.bundle.name} line={lossFor?.line} ownerLabel={label}
        onApproved={async (r) => {
          const row = lossFor.row;
          setLossFor(null);
          if (r.evaluation.state === "READY") await setProductBundleStatus(token, row.bundle._id, "active");
          load();
        }} />
    </div>
  );
};

export default ProductBundleManager;
